import { useRouter } from 'next/router';
import { FC, PropsWithChildren,useEffect } from 'react';
import { v5 as uuidv5 } from 'uuid';

import { isProduction } from '../env';
import useUser from '../sdk/use-user';
import dayjs from '../utils/date';

const sardineClientId = process.env.NEXT_PUBLIC_SARDINE_CLIENT_ID;
const kycDeactivated = process.env.NEXT_PUBLIC_KYC_DEACTIVATED;

const sardineHost = isProduction() ? 'api.sardine.ai' : 'api.dev.sardine.ai';
const sardineUrl = `https://${sardineHost}`;
const sardineScriptUrl = `${sardineUrl}/assets/loader.min.js`;

type SardineConfig = {
  flow: string;
  userIdHash: string | null;
  sessionKey: string;
};

type SardineContext = {
  updateConfig: (config: SardineConfig) => void;
};

type LoadSardineContext = (
  path: string,
  token: string,
  customerId: string,
) => Promise<SardineContext>;

let context: SardineContext;

const load: LoadSardineContext = (
  path: string,
  sessionKey: string,
  customerId: string,
) => {
  if (context) {
    return Promise.resolve(context);
  }

  return new Promise((resolve) => {
    const loader = document.createElement('script');
    loader.type = 'text/javascript';
    loader.async = true;
    loader.src = sardineScriptUrl;
    loader.addEventListener('load', () => {
      context = (window as any)._Sardine.createContext({
        clientId: sardineClientId,
        sessionKey,
        userIdHash: customerId,
        flow: path,
        environment: isProduction() ? 'production' : 'development',
        parentElement: document.body,
        onDeviceResponse: ({ deviceId }: { deviceId: string }) => {
          console.log(`[sardine] deviceID is ${deviceId}`);
        },
      });
      resolve(context);
    });

    const s = document.getElementsByTagName('script')[0];
    s?.parentNode?.insertBefore(loader, s);
  });
};

const expireMinutes = 20;
const storageKey = 'collection-aot-legend_session-key';
const storageTimeKey = `timeout_${storageKey}`;
const sessionIdNamespace = 'bf3b2517-e152-4878-81df-e5d1895595d1';

const createSessionKey = (idKey: string) => {
  return uuidv5(idKey, sessionIdNamespace);
};

export const isSardineActive = (): boolean => {
  const isDeactivated = kycDeactivated ? kycDeactivated === 'true' : false;
  return !isDeactivated;
};

export const getSessionId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const saveValue = localStorage.getItem(storageKey);
  const saveTimeValue = localStorage.getItem(storageTimeKey);
  if (saveValue && saveTimeValue) {
    const storedTime = dayjs(saveTimeValue);
    const now = dayjs();
    if (!now.isAfter(storedTime.add(expireMinutes, 'minutes'))) {
      return saveValue;
    }
  }

  const time = dayjs().toISOString();
  const uuid = `${time}+(${window.navigator.userAgent})`;
  const value = createSessionKey(uuid);
  localStorage.setItem(storageKey, value);
  localStorage.setItem(storageTimeKey, time);

  return value;
};

export const dropSession = () => {
  localStorage.removeItem(storageKey);
  localStorage.removeItem(storageTimeKey);
};

export const DeviceIntelligenceDetector: FC<
  PropsWithChildren<unknown>
> = () => {
  const { data: magic } = useUser();
  const { pathname } = useRouter();

  const token = magic?.user?.token;
  const issuer = magic?.user?.issuer;
  const sessionKey = getSessionId();

  useEffect(() => {
    if (!sardineClientId || !sessionKey || !issuer) {
      return;
    }

    if (!context) {
      load(pathname, sessionKey, issuer).then(() => {
        console.log('[sardine] load', pathname);
      });
    } else {
      const result = context.updateConfig({
        flow: pathname,
        userIdHash: issuer,
        sessionKey,
      });
      console.log('[sardine] update', result, pathname);
    }
  }, [sessionKey, issuer, pathname]);

  if (!sardineClientId) {
    return null;
  }

  return (
    <noscript>
      {token && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`${sardineUrl}/v1/a.png?clientId=${sardineClientId}&sessionKey=${sessionKey}&flow=${pathname}&ns=1`}
          style={{ display: 'none' }}
          alt=""
        />
      )}
    </noscript>
  );
};
